import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../../auth';
import { getBlogReleasePolicy, getBlogSessionToken } from '../../../Apis/touzenBlogApi';
import { isYowanPreviewActive } from '../../../app/host/yowanPreviewRouting';

const PolicyContext = createContext(null);
const closed = { public_release: false, can_manage: false };
const useStyles = makeStyles((theme) => ({ notice: {
  color: '#654321', fontSize: '1rem', lineHeight: 1.8, marginBottom: theme.spacing(3), overflowWrap: 'anywhere',
  border: '4px double currentColor', padding: theme.spacing(2), boxSizing: 'border-box',
} }));
const isTouzenSurface = () => {
  const host = window.location.hostname.toLowerCase();
  return !(window.__NODE_V11_DOMAIN_APP__ === 'yowan' || /(^|\.)yowan\.jp$/.test(host)
    || ['yowan-stg.shimashou.com', 'yowan-prod.shimashou.com'].includes(host)
    || (['localhost', '127.0.0.1', '::1'].includes(host) && isYowanPreviewActive(window.location.search)));
};

function useLocalPolicy(enabled) {
  const auth = useContext(AuthContext) || {};
  const location = useLocation();
  const touzen = isTouzenSurface();
  const authenticated = Boolean(auth.isAuth);
  const token = authenticated && touzen ? getBlogSessionToken() : '';
  const account = auth.user?.id || auth.user?.uuid || auth.user?.email || '';
  const scope = `${touzen}:${authenticated}:${account}:${location.pathname}`;
  const identity = useRef({ scope, token, generation: 0 });
  if (identity.current.scope !== scope || identity.current.token !== token) identity.current = { scope, token, generation: identity.current.generation + 1 };
  const generation = identity.current.generation;
  const [state, setState] = useState({ generation, ...closed, loading: true, error: null });
  const [, refreshToken] = useState(0);
  useEffect(() => {
    if (!enabled || !touzen) return undefined;
    const controller = new AbortController();
    setState({ generation, ...closed, loading: true, error: null });
    const read = async () => {
      try {
        const data = await getBlogReleasePolicy(controller.signal, { authenticated, expectedToken: token });
        if (controller.signal.aborted || identity.current.generation !== generation || (authenticated && getBlogSessionToken() !== token)) return;
        setState({ generation, ...data, loading: false, error: null });
      } catch (error) {
        if (!controller.signal.aborted && identity.current.generation === generation) setState({ generation, ...closed, loading: false, error });
      }
    };
    read();
    return () => controller.abort();
  }, [enabled, generation, authenticated, token, touzen]);
  useEffect(() => {
    if (!enabled || !authenticated || !touzen) return undefined;
    const changed = (event) => { if (event.key === 'accesstoken' || event.key === null) refreshToken((value) => value + 1); };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [enabled, authenticated, touzen]);
  // Yowan keeps its existing surfaces; the Touzen policy and notice do not apply.
  if (!touzen) return { public_release: true, can_manage: false, loading: false, error: null, applicable: false };
  return state.generation === generation ? state : { ...closed, loading: true, error: null };
}

export function BlogReleasePolicyProvider({ children }) {
  const policy = useLocalPolicy(true);
  return <PolicyContext.Provider value={policy}>{children}</PolicyContext.Provider>;
}

export function useBlogReleasePolicy() {
  const provided = useContext(PolicyContext);
  const local = useLocalPolicy(provided === null);
  return provided || local;
}

export function BlogReleaseNotice() {
  const classes = useStyles();
  const policy = useBlogReleasePolicy();
  return policy.public_release ? null : <Typography component="p" role="note" className={classes.notice}>現在テスト運用中です。関係者のみに対して表示しており、他の会員、一般へのブログ露出はしていません</Typography>;
}
